// react
import { useEffect, useRef } from 'react';

// material ui
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// components
import { formatNumber } from '@/utils/formatters';

// relative
import {
  darkTheme,
  lightTheme,
} from '../../../../../../components/highcharts/themes';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/stock';
import { DateTime } from 'luxon';

interface DataPoint {
  timestamp: string;
  value: number;
}

interface DataShape {
  Data?: DataPoint[];
}

interface Props {
  data?: DataShape;
  isLoading?: boolean;
  unit?: string;
}

const PortfolioAnalysisDataChart = ({
  data,
  isLoading,
  unit = 'kWh',
}: Props) => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const theme = useTheme();
  const seriesData = data?.Data?.map((item): [number, number] => [
    new Date(item.timestamp).getTime(),
    item.value,
  ]).sort((a, b) => a[0] - b[0]);
  const options = {
    chart: {
      zoomType: 'x',
      zooming: { mouseWheel: false },
    },
    title: {
      text: ``,
    },
    xAxis: {
      type: 'datetime',
    },
    yAxis: {
      labels: {
        format: `{text} ${unit}`,
      },
      title: {
        text: '',
      },
    },
    series: [
      {
        name: 'Forbrug',
        data: seriesData,
      },
    ],
    legend: {
      enabled: false,
    },
    tooltip: {
      formatter: function (): string {
        const ctx = this as unknown as {
          x: number;
          y: number;
          point: Highcharts.Point;
        };
        const millis = DateTime.fromMillis(ctx.x);
        const date = millis.setLocale('da-DK').toLocaleString({
          month: 'short',
          day: 'numeric',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'UTC',
        });
        return `
                          <span>${date}</span><br/>
                          <span style="color:${
                            ctx.point.color
                          }">●</span> Forbrug: ${formatNumber(
                            ctx.y,
                          )} ${unit}<br/>
                      `;
      },
    },
  };
  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;

    if (chartComponentRef.current?.chart) {
      const chart = chartComponentRef.current.chart;
      chart.update(
        {
          chart: { backgroundColor: themeOptions.chart.backgroundColor },

          title: { style: { color: themeOptions.title.style.color } },
          xAxis: {
            crosshair: { width: 1, color: 'gray', dashStyle: 'ShortDot' },
            gridLineColor: 'transparent',
            lineColor: themeOptions.xAxis.lineColor,
            labels: { style: { color: themeOptions.xAxis.labels.style.color } },
          },
          yAxis: [
            {
              crosshair: { width: 1, color: 'gray', dashStyle: 'ShortDot' },
              gridLineColor: 'transparent',
              labels: {
                style: { color: themeOptions.yAxis.labels.style.color },
              },
            },
          ],
          legend: {
            itemStyle: { color: themeOptions.legend.itemStyle.color },
          },
          tooltip: {
            backgroundColor: themeOptions.tooltip.backgroundColor,
            style: {
              color: theme.palette.mode === 'dark' ? 'white' : 'black',
            },
          },
        },
        false,
      );
      chart.redraw();
    }
  }, [theme]);
  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      if (isLoading) {
        chart.showLoading('Loading data...');
      } else {
        chart.hideLoading();
      }
    }
  }, [isLoading]);
  return (
    <Box width={'100%'} sx={{ mt: 2 }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartComponentRef}
      />
    </Box>
  );
};

export default PortfolioAnalysisDataChart;
